import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Wand2, Loader2 } from "lucide-react";
import { api } from "../api";

const DEFAULT_SPEC = { mcq: 4, true_false: 3, fill_blank: 2, find_mistake: 2, open: 2, match: 0 };

const shuffled = (arr) => {
  const seen = [...new Set(arr.filter(Boolean))];
  return seen.sort((a, b) => String(a).localeCompare(String(b)));
};

export const SpecPicker = ({ value, onChange, testidPrefix = "spec" }) => {
  const [types, setTypes] = useState([]);
  useEffect(() => { api.get("/worksheet/types").then((r) => setTypes(r.data.types)); }, []);
  const total = Object.values(value).reduce((a, b) => a + (Number(b) || 0), 0);

  return (
    <div className="space-y-3" data-testid={`${testidPrefix}-picker`}>
      <div className="grid sm:grid-cols-2 gap-3">
        {types.map((ty) => (
          <label key={ty.id} className="flex items-center justify-between gap-3 rounded-xl
            bg-white/8 border border-white/15 px-3 py-2">
            <span className="min-w-0">
              <span className="block text-sm font-bold truncate">{ty.label_ar}</span>
              <span className="block text-[11px] text-white/50 truncate" dir="ltr">{ty.label_en}</span>
            </span>
              <input type="number" min="0" max="10" value={value[ty.id] ?? 0}
                data-testid={`${testidPrefix}-${ty.id}`}
                onChange={(e) => onChange({ ...value, [ty.id]: Number(e.target.value) })}
                className="w-16 shrink-0 rounded-lg bg-white/10 border border-white/20 text-white
                  text-sm text-center py-1" />
          </label>
        ))}
      </div>
      <p className="text-xs text-white/55" data-testid={`${testidPrefix}-total`}>
        إجمالي الأسئلة: <b style={{ color: "var(--play-accent)" }}>{total}</b> · 0 يعني لا تُضاف أسئلة من هذا النوع
      </p>
    </div>
  );
};

export const SheetView = ({ sheet, showAnswers = false }) => (  <div className="space-y-6" data-testid="sheet-view">
    <div>
      <h3 className="text-lg font-black">{sheet.title}</h3>
      <p className="text-sm text-white/75 mt-1" dir="ltr">{sheet.instructions_en}</p>
      <p className="text-xs text-white/55">{sheet.instructions_ar}</p>
    </div>
    {sheet.sections.map((sec, si) => (
      <div key={si} data-testid={`sheet-section-${sec.type}`}>
        <div className="font-black text-sm" dir="ltr">{sec.label_en}</div>
        <div className="text-xs text-white/55 mb-2">{sec.label_ar}</div>
        <ol className="space-y-2 ps-5 list-decimal">
          {sec.items.map((it, ii) => (
            <li key={ii} className="text-sm" dir="ltr">
              {it.question}
              {!!(it.options || []).length && (
                <span className="block text-white/70 text-xs mt-1">
                  {it.options.map((o, oi) => `(${String.fromCharCode(97 + oi)}) ${o}`).join("   ")}
                </span>
              )}
              {showAnswers && <span className="block text-xs mt-0.5" style={{ color: "#3ec46d" }}>
                Answer: {it.answer}</span>}
            </li>
          ))}
        </ol>
      </div>
    ))}
  </div>
);

export const SheetSolver = ({ sheet, onSubmit, busy, testid = "sheet-solver" }) => {
  const [answers, setAnswers] = useState({});
  const set = (k, v) => setAnswers((a) => ({ ...a, [k]: v }));
  return (
    <form data-testid={testid} className="space-y-6"
      onSubmit={(e) => { e.preventDefault(); onSubmit(answers); }}>
      <div>
        <p className="text-sm text-white/80" dir="ltr">{sheet.instructions_en}</p>
        <p className="text-xs text-white/55">{sheet.instructions_ar}</p>
      </div>
      {sheet.sections.map((sec, si) => (
        <div key={si} className="space-y-3">
          <div>
            <div className="font-black text-sm" dir="ltr">{sec.label_en}</div>
            <div className="text-xs text-white/55">{sec.label_ar}</div>
          </div>
          {sec.items.map((it, ii) => {
            const key = `${si}-${ii}`;
            const options = sec.type === "true_false" ? ["True", "False"]
              : sec.type === "match" ? shuffled(sec.items.map((x) => x.answer))
                : (it.options || []);
            return (
              <div key={key} className="rounded-xl bg-white/8 border border-white/15 p-3">
                <p className="text-sm font-bold" dir="ltr">{it.question}</p>
                {options.length ? (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {options.map((o) => (
                      <button type="button" key={o} data-testid={`${testid}-${key}-${o}`}
                        onClick={() => set(key, o)}
                        className="rounded-full px-3 py-1.5 text-xs font-bold border transition-colors"
                        style={{
                          background: answers[key] === o ? "var(--play-primary)" : "rgba(255,255,255,.08)",
                          borderColor: answers[key] === o ? "transparent" : "rgba(255,255,255,.2)",
                          color: "#fff",
                        }} dir="ltr">{o}</button>
                    ))}
                  </div>
                ) : (
                  <input className="input mt-2" dir="ltr" data-testid={`${testid}-input-${key}`}
                    value={answers[key] || ""} onChange={(e) => set(key, e.target.value)}
                    placeholder="Write your answer..." />
                )}
              </div>
            );
          })}
        </div>
      ))}
      <button disabled={busy} data-testid={`${testid}-submit`}
        className="play-btn text-white w-full flex items-center justify-center gap-2 disabled:opacity-60"
        style={{ background: "var(--play-primary)" }}>
        {busy ? <Loader2 className="animate-spin" size={16} /> : <Wand2 size={16} />}تسليم الحل
      </button>
    </form>
  );
};

export const useSpec = () => useState(DEFAULT_SPEC);

export const specTotal = (spec) => Object.values(spec).reduce((a, b) => a + (Number(b) || 0), 0);

export const toastSpecError = () => toast.error("اختر عدد الأسئلة لنوع واحد على الأقل");
